/**
 * Page 1 of Create trigger drawer
 * Configure trigger event type
 */
import {Select} from '@wandb/weave/components/Form/Select';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React from 'react';
import {useShallow} from 'zustand/react/shallow';

import {Analytics} from '../../../../services/analytics';
import {Update} from '../../../../util/filters';
import {FilterKey, ValueFilter} from '../../../../util/filterTypes';
import {Banner, BannerFlexWrapper, BannerTextWrapper} from '../../../Banner';
import {SemanticLoader as Loader} from '../../../utility/InstrumentedLoader';
import {SelectEntity} from '../../Queues/SelectEntity';
import {
  ArtifactAliasFilterCreator,
  ArtifactCollectionFilterCreator,
  EventConfigurationProps,
  LinkModelFilterCreator,
} from '../TriggerEventConfiguration';
import {
  EventTriggeringConditionType,
  makeTriggerOptionForSelect,
} from '../TriggerEventTypes';
import {OptionType} from '../TriggersTable/utilTypes';
import * as S from './CreateTriggerDrawerLoaded.styles';
import {EventStoreActions, useEventStore} from './EventStore';
import {RunMetricChangeSection} from './RunMetricChangeSection';
import {RunMetricThresholdSection} from './RunMetricThresholdSection';
import {SeparatedChunk} from './shared';
import {SwitchToBasicButton} from './SwitchToBasicButton';
import {EntryPoint} from './types';

// we don't include this in EventTypeToMetadata since it introduces
// circular dependencies with CreateTriggerLogic.ts/TriggerEventTypes.ts
export const EventTypeToConfigComponent: {
  [key in EventTriggeringConditionType]:
    | React.FC<EventConfigurationProps>
    | undefined;
} = {
  [EventTriggeringConditionType.AddArtifactAlias]: ArtifactAliasFilterCreator,
  [EventTriggeringConditionType.CreateArtifact]:
    ArtifactCollectionFilterCreator,
  [EventTriggeringConditionType.LinkModel]: LinkModelFilterCreator,
  [EventTriggeringConditionType.UpdateArtifactAlias]:
    ArtifactAliasFilterCreator,
  [EventTriggeringConditionType.RunMetric]: RunMetricThresholdSection,
  [EventTriggeringConditionType.RunMetricChange]: RunMetricChangeSection,
};

export const DrawerPageEventConfiguration = () => {
  const eventConfig = useEventStore(useShallow(s => s));

  React.useEffect(() => {
    if (eventConfig.entryPoint) {
      Analytics.track('Automation Creation', {
        type: 'page_view',
        page: 'event_configuration',
        entryPoint: eventConfig.entryPoint,
      });
    }
    // this is just for tracking arrival analytics, so we don't need to
    // re-run this on every event type change. Included entrypoint check
    // since sometimes it wasn't getting initialized in workspaces
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventConfig.entryPoint]);

  const eventType = eventConfig.eventType;
  const EventConfigComponent = eventType
    ? EventTypeToConfigComponent[eventType]
    : null;

  if (eventConfig.loading) {
    return <Loader active name="create-trigger-drawer-event-config" />;
  }

  return (
    <>
      {eventConfig.entryPoint === EntryPoint.WORKSPACE_PANEL && (
        <Tailwind>
          <div className="pt-16">
            <Banner variant="info">
              <BannerFlexWrapper>
                <Icon name="lightbulb-info" />
                <BannerTextWrapper>
                  <div>
                    This setup has been pre-configured to create an alert
                    automation.
                  </div>
                </BannerTextWrapper>
              </BannerFlexWrapper>
            </Banner>
          </div>
        </Tailwind>
      )}
      <S.PageLabelRow>Step 1 of 3 - Event configuration</S.PageLabelRow>
      {eventConfig.entryPoint === EntryPoint.REGISTRY && (
        <Tailwind>
          <div className="pt-16">
            <Banner variant="info">
              <BannerFlexWrapper>
                <Icon name="lightbulb-info" />
                <BannerTextWrapper>
                  <div>
                    <span className="font-semibold">
                      Registry level automation.
                    </span>{' '}
                    Automations are applied to all collections in the registry,
                    including those added in the future.
                  </div>
                </BannerTextWrapper>
              </BannerFlexWrapper>
            </Banner>
          </div>
        </Tailwind>
      )}
      {eventConfig.getShowSelectTeam() && (
        <>
          {eventConfig.entryPoint === EntryPoint.TEAM_MODEL_REGISTRY && (
            <Tailwind>
              <div className="pt-16">
                <Banner variant="info">
                  <BannerFlexWrapper>
                    <Icon name="lightbulb-info" />
                    <BannerTextWrapper>
                      <div>
                        Automations created here are applied to all collections
                        in a selected team's model registry, including future
                        collections.
                      </div>
                    </BannerTextWrapper>
                  </BannerFlexWrapper>
                </Banner>
              </div>
            </Tailwind>
          )}

          <S.LabelRow>
            <S.Label>Team</S.Label>
          </S.LabelRow>
          <SelectEntity
            includeAll={false}
            value={eventConfig.entityName ?? ''}
            onSelectEntity={en => {
              // We can't check purely on "is entityName valid", since that
              // would then prevent the user from changing the entityName
              // once one is picked. unknownEntityName captures that the
              // caller of CreateTriggerDrawer didn't provide an entityName,
              // so we should allow the user to pick & change teams.
              if (eventConfig.allowEntityNameChange) {
                EventStoreActions.setEntityNameAsync(en);
              }
            }}
            includePersonalEntity={false}
          />
          {eventConfig.allowEntityNameChange && (
            <Tailwind>
              <div className="text-moon-500">
                The automation will be applied to the selected team's model
                registry.
              </div>
            </Tailwind>
          )}
        </>
      )}

      <S.LabelRow>
        <S.Label>Event</S.Label>
      </S.LabelRow>
      <Select<OptionType | null>
        options={eventConfig
          .getAvailableConditionTypes()
          .map(makeTriggerOptionForSelect)}
        value={eventType ? makeTriggerOptionForSelect(eventType) : null}
        placeholder="Select an event type..."
        onChange={(d: OptionType | null) => {
          const eventType = d?.value as EventTriggeringConditionType;
          EventStoreActions.setEventType(eventType);

          Analytics.track('Automation Creation', {
            type: 'event_selected',
            eventType,
            entryPoint: eventConfig.entryPoint,
            entityName: eventConfig.entityName,
          });
        }}
      />

      {EventConfigComponent != null && (
        <div>
          <EventConfigComponent
            filters={eventConfig.filters.filters[0]}
            setFilter={(index: number, f: ValueFilter<FilterKey>) => {
              EventStoreActions.setFilters(
                Update.setFilter(eventConfig.filters, [0, index], f)
              );
            }}
            pushFilter={(newFilter: ValueFilter<FilterKey>) => {
              EventStoreActions.setFilters(
                Update.groupPush(eventConfig.filters, [0], newFilter)
              );
            }}
            deleteFilter={(index: number) =>
              EventStoreActions.setFilters(
                Update.groupRemove(eventConfig.filters, [0], index)
              )
            }
            setTriggerScopeType={scopeType =>
              EventStoreActions.setScopeType(scopeType)
            }
            setTriggerScopeID={id => EventStoreActions.setScopeID(id)}
            triggerScopeID={eventConfig.scopeID}
            triggerScopeType={eventConfig.scopeType}
            artifactCollectionID={eventConfig.initProps.artifactCollectionID}
            setTriggerArtifactCollectionName={artifactCollectionName =>
              EventStoreActions.setArtifactCollectionName(
                artifactCollectionName
              )
            }
          />
          <SeparatedChunk>
            <SwitchToBasicButton />
          </SeparatedChunk>
        </div>
      )}
    </>
  );
};
